import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import {
  Box,
  Button,
  Typography,
  TextField,
  CircularProgress,
} from '@mui/material';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import api from '../config/api';
import { useGlobalWebSocket } from '../contexts/GlobalWebSocketContext';
import { useUnreadMessagesContext } from '../contexts/UnreadMessagesContext';
import useTabNotification from '../hooks/useTabNotification';
import useBrowserNotifications from '../hooks/useBrowserNotifications';
import { DOCUMENT } from '../constants/documentTheme';

function chatDisplayName(user) {
  const named = [user?.first_name, user?.last_name]
    .map((part) => String(part || '').trim())
    .filter(Boolean)
    .join(' ');
  if (named) return named;
  return cleanChatLabel(user?.username) || 'You';
}

function cleanChatLabel(name) {
  const s = String(name || '').trim();
  if (!s) return '';
  return s.replace(/_[a-z0-9]{5,}$/i, '') || s;
}

const TripGroupChat = ({ tripId, onUnreadCountChange, currentUser, isTabActive = true, tripTitle = '' }) => {
  const { t } = useTranslation();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [lastReadMessage, setLastReadMessage] = useState(null);
  const [isBrowserTabActive, setIsBrowserTabActive] = useState(!document.hidden);
  const messagesEndRef = useRef(null);
  const paneRef = useRef(null);
  const [paneHeight, setPaneHeight] = useState(Math.max(560, Math.floor((typeof window !== 'undefined' ? window.innerHeight : 900) * 0.72)));
  
  const { connected, sendMessage, addEventListener, subscribeToTrip, unsubscribeFromTrip } = useGlobalWebSocket();
  const { showChatNotification, requestPermission, permission } = useBrowserNotifications();
  const { markAsRead } = useUnreadMessagesContext();

  // Use tab notification hook - only show browser tab notifications if chat tab is not active
  useTabNotification(isTabActive ? 0 : unreadCount, isBrowserTabActive);

  // Helper function to mark messages as read in both local state and global context
  const markMessagesAsRead = useCallback((messageId) => {
    if (messageId) {
      setLastReadMessage(messageId);
      localStorage.setItem(`lastReadMessage_${tripId}`, messageId.toString());
      // Also mark as read in the centralized context to clear global notifications
      markAsRead(tripId, messageId);
    }
  }, [tripId, markAsRead]);

  // Request notification permission on mount
  useEffect(() => {
    if (permission === 'default') {
      requestPermission();
    }
  }, [permission, requestPermission]);

  // Notify parent component of unread count changes
  useEffect(() => {
    if (onUnreadCountChange) {
      onUnreadCountChange(unreadCount);
    }
  }, [unreadCount, onUnreadCountChange, isTabActive]);

  // Track browser tab visibility
  useEffect(() => {
    const handleVisibilityChange = () => {
      const isBrowserActive = !document.hidden;
      setIsBrowserTabActive(isBrowserActive);
      
      if (isBrowserActive && isTabActive) {
        // Both browser tab and chat tab are active, mark all messages as read
        setUnreadCount(0);
        if (messages.length > 0) {
          markMessagesAsRead(messages[messages.length - 1].id);
        }
      }
    };

    const handleFocus = () => {
      setIsBrowserTabActive(true);
      if (isTabActive) {
        setUnreadCount(0);
        if (messages.length > 0) {
          markMessagesAsRead(messages[messages.length - 1].id);
        }
      }
    };

    const handleBlur = () => {
      setIsBrowserTabActive(false);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
    };
  }, [messages, isTabActive, markMessagesAsRead]);

  // Clear unread count when chat tab becomes active
  useEffect(() => {
    if (isTabActive && isBrowserTabActive) {
      setUnreadCount(0);
      if (messages.length > 0) {
        const latestMessageId = messages[messages.length - 1].id;
        markMessagesAsRead(latestMessageId);
      }
    }
  }, [isTabActive, isBrowserTabActive, messages, markMessagesAsRead]);

  // Listen for reconnect/disconnect events and fetch missed messages
  useEffect(() => {
    if (!addEventListener) return;
    const handleReconnect = async () => {
      try {
        // Always fetch the full message list on reconnect
        const response = await api.get(`/trips/${tripId}/messages`);
        if (response.data) {
          setMessages(response.data);
        }
      } catch (err) {
        if (err?.response?.status === 403) {
          setTimeout(() => {
            window.location.href = '/login';
          }, 2000);
        } else {
          setLoading(false);
        }
      }
    };
    const handleDisconnect = () => {
      setLoading(false);
    };
    const removeReconnect = addEventListener('reconnect', handleReconnect);
    const removeDisconnect = addEventListener('disconnect', handleDisconnect);
    return () => {
      if (removeReconnect) removeReconnect();
      if (removeDisconnect) removeDisconnect();
    };
  }, [addEventListener, tripId]);

  // Robust error handling for initial and manual fetches
  const fetchInitialMessages = async () => {
    try {
      const response = await api.get(`/trips/${tripId}/messages`);
      setMessages(response.data);
      setLoading(false);
      // If tab is active, mark as read
      if (isTabActive && !document.hidden && response.data.length > 0) {
        const latestMessageId = response.data[response.data.length - 1].id;
        markMessagesAsRead(latestMessageId);
      }
    } catch (err) {
      console.error('Error fetching initial messages:', err);
      setError('Failed to load messages');
      setLoading(false);
      if (err?.response?.status === 403) {
        setTimeout(() => {
          window.location.href = '/login';
        }, 2000);
      }
    }
  };

  // Subscribe to the trip and initial message fetch on component mount
  useEffect(() => {
    if (subscribeToTrip) {
      console.log(`📨 [TripGroupChat] Subscribing to trip ${tripId}`);
      subscribeToTrip(tripId);
    }
    fetchInitialMessages();
    return () => {
      if (unsubscribeFromTrip) {
        console.log(`📨 [TripGroupChat] Unsubscribing from trip ${tripId}`);
        unsubscribeFromTrip(tripId);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tripId]); // Only depend on tripId to prevent infinite subscription loops

  // Set up message listener for real-time WebSocket messages
  useEffect(() => {
    if (!addEventListener) return;

    // Existing message event handler
    const removeListener = addEventListener('message', (data) => {
      console.log('[TripGroupChat] WebSocket message received:', data.message);
      setMessages(prev => {
        // Check for duplicate messages by ID
        const existingMessage = prev.find(msg => msg.id === data.message.id);
        if (existingMessage && !existingMessage.isOptimistic) {
          console.log('[TripGroupChat] Duplicate message ignored:', data.message.id);
          return prev; // Message already exists and is not optimistic
        }

        // Remove any optimistic messages from the same user with similar content and timing
        const filteredMessages = prev.filter(msg => {
          if (!msg.isOptimistic) return true; // Keep all real messages
          const isFromSameUser = msg.user_id === data.message.user_id;
          const isSameContent = msg.content === data.message.content;
          const messageTime = new Date(data.message.created_at).getTime();
          const optimisticTime = new Date(msg.created_at).getTime();
          const isRecent = Math.abs(messageTime - optimisticTime) < 30000;
          if (isFromSameUser && isSameContent && isRecent) {
            console.log('[TripGroupChat] Removing optimistic message:', msg);
          }
          return !(isFromSameUser && isSameContent && isRecent);
        });

        const newMessages = [...filteredMessages, data.message];
        console.log('[TripGroupChat] Messages array after update:', newMessages);

        // Only count as unread if:
        // 1. Chat tab is not currently active OR browser tab is hidden
        // 2. Message is not from the current user
        const shouldCountAsUnread = (!isTabActive || !isBrowserTabActive) && 
                                  data.message.user_id !== currentUser?.id;
        if (shouldCountAsUnread) {
          setUnreadCount(prevCount => {
            const newCount = prevCount + 1;
            console.log('[TripGroupChat] Unread count updated:', newCount);
            return newCount;
          });
          if (permission === 'granted' && data.message.user_id !== currentUser?.id) {
            showChatNotification(
              data.message.sender_name,
              data.message.content,
              tripTitle
            );
          }
        } else {
          console.log('[TripGroupChat] Marking message as read immediately:', { 
            messageId: data.message.id, 
            tripId, 
            isTabActive, 
            isBrowserTabActive,
            shouldCountAsUnread
          });
          markMessagesAsRead(data.message.id);
        }
        return newMessages;
      });
    });

    return () => {
      if (removeListener) removeListener();
    };
  }, [addEventListener, isTabActive, isBrowserTabActive, currentUser?.id, permission, showChatNotification, tripTitle, markMessagesAsRead, tripId]);

  // Update unread count when messages change and tab is not active
  useEffect(() => {
    if ((!isTabActive || !isBrowserTabActive) && lastReadMessage) {
      const unreadMessages = messages.filter(msg => 
        msg.id > lastReadMessage && msg.user_id !== currentUser?.id
      );
      setUnreadCount(unreadMessages.length);
    }
  }, [messages, lastReadMessage, isTabActive, isBrowserTabActive, currentUser?.id]);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const handleSendMessage = useCallback(async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    const messageContent = newMessage.trim();
    const tempId = `temp_${Date.now()}_${Math.random()}`;
    const optimisticMessage = {
      id: tempId,
      trip_id: parseInt(tripId),
      user_id: currentUser?.id,
      sender_name: chatDisplayName(currentUser),
      content: messageContent,
      created_at: new Date().toISOString(),
      isOptimistic: true,
      isQueued: !connected
    };

    setMessages(prev => [...prev, optimisticMessage]);
    setNewMessage('');

    if (!connected) {
      // If not connected, try HTTP fallback
      try {
        await api.post(`/chat/${tripId}/messages`, { content: messageContent });
      } catch (err) {
        console.error('Failed to send message via HTTP:', err);
        setError('Failed to send message');
      }
      return;
    }

    try {
      const messageSent = sendMessage(tripId, messageContent);
      if (!messageSent) {
        // WebSocket failed, try HTTP fallback
        await api.post(`/chat/${tripId}/messages`, { content: messageContent });
      }
    } catch (err) {
      console.error('Failed to send message:', err);
      setError('Failed to send message');
    }
  }, [newMessage, sendMessage, tripId, currentUser, connected]);

  useLayoutEffect(() => {
    if (!isTabActive) return undefined;

    const updatePaneHeight = () => {
      const pane = paneRef.current;
      if (!pane) return;
      const top = pane.getBoundingClientRect().top;
      // Fill remaining viewport; keep a small bottom gutter for the page chrome.
      setPaneHeight(Math.max(560, Math.floor(window.innerHeight - top - 8)));
    };

    const frame = window.requestAnimationFrame(updatePaneHeight);
    const timeout = window.setTimeout(updatePaneHeight, 350);
    window.addEventListener('resize', updatePaneHeight);
    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(timeout);
      window.removeEventListener('resize', updatePaneHeight);
    };
  }, [isTabActive, loading]);

  return (
    <Box>
      <Box
        ref={paneRef}
        sx={{
          display: 'grid',
          border: `1px solid ${DOCUMENT.ruleHard}`,
          bgcolor: DOCUMENT.surface,
          minHeight: 560,
          height: paneHeight,
          maxHeight: paneHeight,
          gridTemplateRows: '1fr auto',
        }}
      >
        <Box
          sx={{
            overflowY: 'auto',
            overflowX: 'hidden',
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            bgcolor: '#F5F5F7',
          }}
        >
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
              <CircularProgress size={40} />
            </Box>
          ) : error ? (
            <Box sx={{ textAlign: 'center', p: 2 }}>
              <Typography color="error" variant="caption">{error}</Typography>
            </Box>
          ) : messages.length === 0 ? (
            <Box sx={{ textAlign: 'center', p: 4, m: 'auto' }}>
              <Typography sx={{ color: DOCUMENT.ink3, fontSize: 14 }}>
                {t('chat.noMessages')}. {t('chat.startConversation')}!
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, px: 1.5, py: 1.75 }}>
              {messages.map((msg, index) => {
                const isOwnMessage = Number(msg.user_id) === Number(currentUser?.id);
                const prev = messages[index - 1];
                const next = messages[index + 1];
                const sameAsPrev = prev && Number(prev.user_id) === Number(msg.user_id);
                const sameAsNext = next && Number(next.user_id) === Number(msg.user_id);
                const showSender = !isOwnMessage && !sameAsPrev;
                const senderLabel = cleanChatLabel(msg.sender_name);

                return (
                  <Box
                    key={msg.id}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: isOwnMessage ? 'flex-end' : 'flex-start',
                      mt: sameAsPrev ? 0.25 : 1,
                      opacity: msg.isOptimistic ? 0.7 : 1,
                      maxWidth: '100%',
                    }}
                  >
                    {showSender ? (
                      <Typography
                        sx={{
                          fontSize: 11,
                          color: DOCUMENT.ink3,
                          mb: 0.35,
                          ml: 1.25,
                          maxWidth: '78%',
                        }}
                      >
                        {senderLabel}
                        {msg.channel && msg.channel !== 'app' ? (
                          <Box component="span" sx={{ color: DOCUMENT.ink3, ml: 0.5 }}>
                            · {msg.channel === 'whatsapp'
                              ? t('chat.channelWhatsapp', { defaultValue: 'WhatsApp' })
                              : msg.channel}
                          </Box>
                        ) : null}
                      </Typography>
                    ) : null}
                    <Box
                      sx={{
                        maxWidth: { xs: '82%', sm: '72%' },
                        px: 1.5,
                        py: 1,
                        bgcolor: isOwnMessage ? '#007AFF' : '#E9E9EB',
                        color: isOwnMessage ? '#fff' : '#111',
                        borderRadius: isOwnMessage
                          ? sameAsPrev && sameAsNext
                            ? '18px 6px 6px 18px'
                            : sameAsPrev
                              ? '18px 6px 18px 18px'
                              : sameAsNext
                                ? '18px 18px 6px 18px'
                                : '18px 18px 6px 18px'
                          : sameAsPrev && sameAsNext
                            ? '6px 18px 18px 6px'
                            : sameAsPrev
                              ? '6px 18px 18px 18px'
                              : sameAsNext
                                ? '18px 18px 18px 6px'
                                : '18px 18px 18px 6px',
                        boxShadow: 'none',
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 15,
                          lineHeight: 1.4,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          color: 'inherit',
                        }}
                      >
                        {msg.content}
                      </Typography>
                    </Box>
                    {!sameAsNext ? (
                      <Typography
                        sx={{
                          fontSize: 10,
                          color: DOCUMENT.ink3,
                          mt: 0.35,
                          mx: 1,
                        }}
                      >
                        {format(new Date(msg.created_at), 'EEE HH:mm')}
                        {msg.isOptimistic ? ' ···' : ''}
                      </Typography>
                    ) : null}
                  </Box>
                );
              })}
              <div ref={messagesEndRef} />
            </Box>
          )}
        </Box>

        <Box
          component="form"
          onSubmit={handleSendMessage}
          sx={{
            display: 'flex',
            gap: 1,
            px: 1.5,
            py: 1.25,
            borderTop: `1px solid ${DOCUMENT.rule}`,
            bgcolor: DOCUMENT.surface,
            alignItems: 'flex-end',
          }}
        >
          <TextField
            fullWidth
            multiline
            maxRows={4}
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSendMessage(e);
              }
            }}
            placeholder={t('chat.messageTheTribe')}
            variant="standard"
            InputProps={{ disableUnderline: true }}
            sx={{
              bgcolor: '#F2F2F7',
              borderRadius: '20px',
              px: 1.75,
              py: 0.75,
              '& .MuiInputBase-input': {
                fontSize: 15,
                color: DOCUMENT.ink,
                py: 0.25,
              },
            }}
          />
          <Button
            type="submit"
            aria-label={t('chat.send')}
            disabled={!newMessage.trim() || loading}
            sx={{
              bgcolor: '#007AFF',
              color: '#fff',
              border: 0,
              borderRadius: '20px',
              fontSize: 13,
              fontWeight: 600,
              textTransform: 'none',
              px: 2,
              py: 1,
              minWidth: 0,
              mb: 0.15,
              '&:hover': { bgcolor: '#0066D6' },
              '&.Mui-disabled': { bgcolor: '#C7C7CC', color: '#fff' },
            }}
          >
            {t('chat.send')}
          </Button>
        </Box>
      </Box>
      {!connected && (
        <Typography sx={{ mt: 1, fontSize: 11, color: DOCUMENT.ink3 }}>
          {t('chat.reconnecting')}
        </Typography>
      )}
    </Box>
  );
};

export default TripGroupChat;
